import styled from 'styled-components';
import { compareObjects } from '../../../functions/utils/comapareObjects';
import {
  LauncherStatesData,
  launcherStatesOrder,
  useLauncherStatesStore,
} from '../../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { DropdownInput } from '../../common/baseInputs/DropdownInput';
import { useScene } from '../../../context/sceneContext';
import { stateViewsInfo } from '../../../info/stateViewsInfo';

export const AdvancedLauncherStatesPanel = () => {
  const scene = useScene();
  const [launcherStates, setLauncherStatesData] = useLauncherStatesStore<LauncherStatesData>();
  const [UIData, setUIData] = useUIStore<UIData>();

  const columnSize = [160, 120, 170, 120];

  return (
    <Container>
      <Row>
        <Cell style={{ width: '100%', borderBottom: '2px solid var(--neutral-3)' }}>
          <span style={{ color: 'var(--main-1)', marginRight: '10px' }}>Current View:</span>
          <span style={{ 
            color: UIData.currentStateView ? 'var(--primary-1)' : 'var(--main-2)',
            fontWeight: UIData.currentStateView ? 'bold' : 'normal'
          }}>
            {UIData.currentStateView || 'None'}
          </span>
        </Cell>
      </Row>

      <Row style={{ color: 'var(--main-1)' }}>
        <Cell
          style={{
            width: columnSize[0] + columnSize[1] + 'px',
          }}
        >
          Display
        </Cell>
        <Cell
          style={{
            width: columnSize[2] + columnSize[3] + 'px',
          }}
        >
          Edit
        </Cell>
      </Row>
      <Row style={{ color: 'var(--main-1)' }}>
        <Cell style={{ width: columnSize[0] + 'px' }}>State</Cell>
        <Cell style={{ width: columnSize[1] + 'px' }}>Substate</Cell>
        <Cell style={{ width: columnSize[2] + 'px' }}>Visibilty</Cell>
        <Cell style={{ width: columnSize[3] + 'px' }}>Current State</Cell>
      </Row>

      <Row>
        {/* State */}
        <Cell style={{ width: columnSize[0] + 'px' }}></Cell>
        {/* Substate */}
        <Cell style={{ width: columnSize[1] + 'px' }}>
          <TextButton onClick={() => scene.resetLauncherStates()}>Reset all</TextButton>
        </Cell>
        {/* Direct Visibility */}
        <Cell style={{ width: columnSize[2] + 'px' }}>
          {UIData.selectedElementIDs.length > 0 && (
            <DropdownInput
              label="Default"
              value={scene.getSelectedElementsDirectVisibility([null, null])}
              options={[
                {
                  value: undefined,
                  label: '',
                  iconClassName: 'icon-anytask',
                },
                {
                  value: true,
                  label: '',
                  iconClassName: 'icon-visible',
                },
                {
                  value: false,
                  label: '',
                  iconClassName: 'icon-notvisible',
                },
              ]}
              onChange={(newValue) =>
                scene.setSelectedElementsVisibility(newValue as boolean | undefined, [null, null])
              }
              isSearchable={false}
              isArrowVisible={false}
              canBeCleared={false}
              textColor={(() => {
                const value = scene.getSelectedElementsDirectVisibility([null, null]);
                if (value === undefined) return 'var(--main-2)';
                if (value === false) return 'var(--error-1)';
                return 'var(--main-1)';
              })()}
              textAlign="center"
              style={{ width: '90px' }}
            />
          )}
        </Cell>
        {/* Current State */}
        <Cell style={{ width: columnSize[3] + 'px' }}>
          {UIData.selectedElementIDs.length > 0 && (
            <TextButton
              $isActive={UIData?.currentLauncherState[0] === null}
              onClick={() =>
                setUIData({
                  currentLauncherState: [null, null],
                })
              }
            >
              Default
            </TextButton>
          )}
        </Cell>
      </Row>

      {launcherStatesOrder.map(
        (launcherState) =>
          launcherState !== 'failReason' && (
            <Row key={launcherState}>
              {/* State */}
              <Cell style={{ width: columnSize[0] + 'px' }}>
                <span
                  style={{
                    color: UIData.currentLauncherState[0] === launcherState ? 'var(--primary-1)' : undefined,
                  }}
                >
                  {launcherState}
                </span>
              </Cell>
              {/* Substate */}
              <Cell style={{ width: columnSize[1] + 'px' }}>
                <>
                  <TextButton
                    $isActive={launcherStates[launcherState] === true}
                    $activeColor="var(--main-1)"
                    onClick={() => {
                      scene.setLauncherStates({
                        ...launcherStates,
                        [launcherState]: true,
                      });
                    }}
                  >
                    True
                  </TextButton>
                  <TextButton
                    $isActive={launcherStates[launcherState] === false}
                    $activeColor="var(--main-1)"
                    onClick={() => {
                      scene.setLauncherStates({
                        ...launcherStates,
                        [launcherState]: false,
                      });
                    }}
                  >
                    False
                  </TextButton>
                </>
              </Cell>
              {/* Direct Visibility */}
              <Cell style={{ width: columnSize[2] + 'px' }}>
                {UIData.selectedElementIDs.length > 0 && (
                  <>
                    <DropdownInput
                      label="True"
                      value={scene.getSelectedElementsDirectVisibility([launcherState, 'true'])}
                      options={[
                        {
                          value: undefined,
                          label: '',
                          iconClassName: 'icon-anytask',
                        },
                        {
                          value: true,
                          label: '',
                          iconClassName: 'icon-visible',
                        },
                        {
                          value: false,
                          label: '',
                          iconClassName: 'icon-notvisible',
                        },
                      ]}
                      onChange={(newValue) =>
                        scene.setSelectedElementsVisibility(newValue as boolean | undefined, [launcherState, 'true'])
                      }
                      isSearchable={false}
                      isArrowVisible={false}
                      canBeCleared={false}
                      textColor={(() => {
                        const value = scene.getSelectedElementsDirectVisibility([launcherState, 'true']);
                        if (value === undefined) return 'var(--main-2)';
                        if (value === false) return 'var(--error-1)';
                        return 'var(--main-1)';
                      })()}
                      textAlign="center"
                    />
                    <DropdownInput
                      label="False"
                      value={scene.getSelectedElementsDirectVisibility([launcherState, 'false'])}
                      options={[
                        {
                          value: undefined,
                          label: '',
                          iconClassName: 'icon-anytask',
                        },
                        {
                          value: true,
                          label: '',
                          iconClassName: 'icon-visible',
                        },
                        {
                          value: false,
                          label: '',
                          iconClassName: 'icon-notvisible',
                        },
                      ]}
                      onChange={(newValue) =>
                        scene.setSelectedElementsVisibility(newValue as boolean | undefined, [launcherState, 'false'])
                      }
                      isSearchable={false}
                      isArrowVisible={false}
                      canBeCleared={false}
                      textColor={(() => {
                        const value = scene.getSelectedElementsDirectVisibility([launcherState, 'false']);
                        if (value === undefined) return 'var(--main-2)';
                        if (value === false) return 'var(--error-1)';
                        return 'var(--main-1)';
                      })()}
                      textAlign="center"
                    />
                  </>
                )}
              </Cell>
              {/* Current State */}
              <Cell style={{ width: columnSize[3] + 'px' }}>
                {UIData.selectedElementIDs.length > 0 && (
                  <>
                    <TextButton
                      $isActive={compareObjects(UIData.currentLauncherState, [launcherState, 'true'])}
                      onClick={() =>
                        setUIData({
                          currentLauncherState: [launcherState, 'true'],
                        })
                      }
                    >
                      True
                    </TextButton>
                    <TextButton
                      $isActive={compareObjects(UIData.currentLauncherState, [launcherState, 'false'])}
                      onClick={() =>
                        setUIData({
                          currentLauncherState: [launcherState, 'false'],
                        })
                      }
                    >
                      False
                    </TextButton>
                  </>
                )}
              </Cell>
            </Row>
          )
      )}
    </Container>
  );
};

const Container = styled.div`
  min-width: 150px;
  padding: 5px;

  font-family: var(--font-1);
  font-size: 12px;
  color: var(--main-2);

  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  height: 25px;
  display: flex;

  &:hover {
    background-color: var(--neutral-1);
  }
`;

const Cell = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--neutral-3);
  padding: 5px;
`;

const TextButton = styled.div<{ $isActive?: boolean; $activeColor?: string }>`
  cursor: pointer;

  width: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 12px;
  color: var(--main-2);
  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);

  transition:
    background-color var(--transition-2),
    color var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  ${({ $isActive, $activeColor }) =>
    $isActive &&
    `
      border-color: ${$activeColor ? $activeColor : 'var(--primary-1)'};
      color: ${$activeColor ? $activeColor : 'var(--primary-1)'};
      &:hover {
        color: ${$activeColor ? $activeColor : 'var(--primary-1)'};
      }
    `}
`;

const VisibilityIndicator = styled.div`
  display: flex;

  span {
    font-size: 12px;
    margin-right: 5px;
  }
`;
